import React, { useState } from "react";
import { FiSearch } from "react-icons/fi";
import { MdCheckCircle, MdCancel, MdDeleteForever } from "react-icons/md";
import Card from "../../../../components/card";
import api from "../../../../services/axios";
import { Billing, BillingStatusName, BillingTypeName } from "../../../../interfaces/Financial";
import { usePermissions } from "../../../../auth/PermissionProvider";
import { Permissions } from "../../../../auth/permissions";
import { toast } from "react-toastify";
import InvoiceDocModal from "./InvoiceDocModal";
import SiimpStatus from "./SiimpStatus";

interface BillingTableProps {
  billings: Billing[];
  onRefresh: () => void;
  onEditServiceOrder?: (billing: Billing) => void;
}

const statusColors: Record<number, string> = {
  1: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  2: "bg-dv-blue-50 text-dv-blue-700 dark:bg-dv-blue-900/40 dark:text-dv-blue-300",
  3: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  4: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  5: "bg-dv-cyan-50 text-dv-cyan-700 dark:bg-dv-cyan-900/40 dark:text-dv-cyan-300",
};

const BillingTable: React.FC<BillingTableProps> = ({ billings, onRefresh, onEditServiceOrder }) => {
  const { can } = usePermissions();
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Billing | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [jaCanceladoNoSiimp, setJaCanceladoNoSiimp] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Billing | null>(null);
  const [invoiceTarget, setInvoiceTarget] = useState<Billing | null>(null);

  // Excluir é hard delete — só quem tem `financial.admin` vê o botão.
  const canDelete = can(Permissions.FinancialAdmin);

  const filtered = billings.filter((b) =>
    [b.clientName, b.projectName ?? "", b.statusName, b.billingTypeName, b.invoiceNumber ?? ""]
      .some((v) => v.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const fmt = (value: number) =>
    value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const handleAction = async (
    action: "emit" | "charge" | "receive",
    billingId: number
  ) => {
    const urlMap = {
      emit: `/Financial/EmitInvoice/${billingId}`,
      charge: `/Financial/Charge/${billingId}`,
      receive: `/Financial/MarkAsReceived/${billingId}`,
    };
    const successMsg = {
      emit: "Invoice emitted successfully!",
      charge: "Billing sent to client!",
      receive: "Payment marked as received!",
    };

    setLoadingId(billingId);
    try {
      await api.post(urlMap[action]);
      toast.success(successMsg[action], { position: "top-right", autoClose: 4000 });
      onRefresh();
    } catch (error: any) {
      const message = error?.response?.data || "Operation failed.";
      toast.error(typeof message === "string" ? message : "Operation failed.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setLoadingId(null);
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    const noSiimp = cancelTarget.siimpEtapa > 0;
    if (noSiimp && !jaCanceladoNoSiimp) return;
    setLoadingId(cancelTarget.billingId);
    try {
      await api.post(`/Financial/Cancel/${cancelTarget.billingId}`, {
        reason: cancelReason.trim(),
        ...(noSiimp ? { jaCanceladoNoSiimp: true } : {}),
      });
      toast.success("Billing cancelled.", { position: "top-right", autoClose: 4000 });
      setCancelTarget(null);
      setCancelReason("");
      setJaCanceladoNoSiimp(false);
      onRefresh();
    } catch (error: any) {
      const message = error?.response?.data || "Failed to cancel billing.";
      toast.error(typeof message === "string" ? message : "Failed to cancel billing.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setLoadingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setLoadingId(deleteTarget.billingId);
    try {
      await api.delete(`/Financial/Billings/${deleteTarget.billingId}`);
      toast.success("Lançamento excluído.", { position: "top-right", autoClose: 4000 });
      setDeleteTarget(null);
      onRefresh();
    } catch (error: any) {
      const message = error?.response?.data || "Falha ao excluir o lançamento.";
      toast.error(typeof message === "string" ? message : "Falha ao excluir o lançamento.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div>
      <div className="relative mb-4 w-full md:max-w-md">
        <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
        <label htmlFor="billing-search" className="sr-only">Buscar cobrança</label>
        <input
          id="billing-search"
          type="text"
          placeholder="Buscar cobrança..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
        />
      </div>

      <Card extra={"w-full p-5 sm:p-6"}>
        <div className="relative mb-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-navy-700 dark:text-white">
            Cobranças
          </h3>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-white/10">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-gray-50 dark:bg-navy-900">
                {["Cliente", "Projeto", "Valor", "Tipo", "Status", "Nota fiscal", "Ações"].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-10 text-center text-sm font-medium text-gray-500 dark:text-gray-400"
                  >
                    Nenhuma cobrança encontrada para o período selecionado.
                  </td>
                </tr>
              ) : (
                filtered.map((billing) => {
                  const isLoading = loadingId === billing.billingId;
                  return (
                    <tr
                      key={billing.billingId}
                      className="border-b border-gray-100 last:border-0 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700"
                    >
                      <td className="px-4 py-3 text-sm text-navy-700 dark:text-white">
                        {billing.clientName}
                      </td>
                      <td className="px-4 py-3 text-sm text-navy-700 dark:text-white">
                        {billing.projectName || (
                          <span className="italic text-gray-500 dark:text-gray-400">— nível cliente —</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold tabular-nums text-navy-700 dark:text-white">
                        {fmt(billing.amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {BillingTypeName[billing.billingType] ?? billing.billingTypeName}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusColors[billing.status] ?? ""}`}
                        >
                          {BillingStatusName[billing.status] ?? billing.statusName}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums text-gray-700 dark:text-gray-300">
                        {billing.invoiceNumber ?? "—"}
                        <div>
                          <SiimpStatus billing={billing} onChange={onRefresh} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          {/* Fase 1 — pendente de emissão de NF (gerada / OS) */}
                          {billing.phase === 1 && (
                            <>
                              {billing.status === 5 && onEditServiceOrder && (
                                <button
                                  disabled={isLoading}
                                  onClick={() => onEditServiceOrder(billing)}
                                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.97] disabled:opacity-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
                                >
                                  Editar OS
                                </button>
                              )}
                              {/* Já enviada ao SIIMP: a etapa/erro aparece na coluna da NF. */}
                              {!billing.siimpEtapa && !billing.siimpErro && (
                              <button
                                disabled={isLoading}
                                onClick={() => setInvoiceTarget(billing)}
                                className="rounded-lg bg-dv-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-dv-blue-700 active:scale-[.97] disabled:opacity-50 dark:bg-dv-blue-500 dark:hover:bg-dv-blue-600"
                              >
                                Emitir NF
                              </button>
                              )}
                            </>
                          )}
                          {/* Fase 2 — faturada (NF emitida), aguardando recebimento */}
                          {billing.phase === 2 && (
                            <button
                              disabled={isLoading}
                              onClick={() => handleAction("receive", billing.billingId)}
                              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-emerald-700 active:scale-[.97] disabled:opacity-50"
                            >
                              Marcar recebido
                            </button>
                          )}
                          {/* Cancelar — enquanto não recebida/cancelada */}
                          {(billing.phase === 1 || billing.phase === 2) && (
                            <button
                              disabled={isLoading}
                              onClick={() => { setCancelTarget(billing); setCancelReason(""); }}
                              className="rounded-lg bg-red-500 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-red-600 active:scale-[.97] disabled:opacity-50"
                            >
                              Cancelar
                            </button>
                          )}
                          {billing.status === 3 && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                              <MdCheckCircle className="h-3.5 w-3.5" aria-hidden="true" /> Recebida
                            </span>
                          )}
                          {billing.status === 4 && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 dark:text-red-400" title={billing.cancelReason ?? ""}>
                              <MdCancel className="h-3.5 w-3.5" aria-hidden="true" /> Cancelada
                            </span>
                          )}
                          {/* Excluir definitivamente (limpar lançamento errado) — só Admin */}
                          {canDelete && (
                            <button
                              disabled={isLoading}
                              onClick={() => setDeleteTarget(billing)}
                              title="Excluir definitivamente"
                              aria-label="Excluir definitivamente"
                              className="rounded-lg border border-red-200 px-2 py-1.5 text-xs font-semibold text-red-600 transition-all hover:bg-red-50 active:scale-[.97] disabled:opacity-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
                            >
                              <MdDeleteForever className="h-4 w-4" aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal documento NF */}
      {invoiceTarget && (
        <InvoiceDocModal
          billingId={invoiceTarget.billingId}
          clientId={invoiceTarget.clientId}
          clientName={invoiceTarget.clientName}
          amount={invoiceTarget.amount}
          billingType={invoiceTarget.billingType}
          monthRef={invoiceTarget.monthRef}
          yearRef={invoiceTarget.yearRef}
          onClose={() => setInvoiceTarget(null)}
          onSuccess={onRefresh}
        />
      )}

      {/* Modal de confirmação de cancelamento */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-dv-lg dark:bg-navy-800">
            <h3 className="mb-2 text-lg font-bold text-navy-700 dark:text-white">
              Cancelar cobrança
            </h3>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              Você está prestes a cancelar a cobrança de{" "}
              <span className="font-semibold text-navy-700 dark:text-white">
                {cancelTarget.projectName || cancelTarget.clientName}
              </span>
              {cancelTarget.projectName ? ` (${cancelTarget.clientName})` : " (nível cliente)"}. Informe o motivo.
            </p>
            {cancelTarget.siimpEtapa > 0 && (
              <div className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                <p role="alert">A NFS-e e a fatura estão no SIIMP. Cancele lá antes.</p>
                <div className="mt-2 flex items-start gap-2">
                  <input
                    id="cancel-siimp"
                    type="checkbox"
                    checked={jaCanceladoNoSiimp}
                    onChange={(e) => setJaCanceladoNoSiimp(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 focus-visible:ring-2 focus-visible:ring-dv-blue-500 dark:border-white/20"
                  />
                  <label htmlFor="cancel-siimp" className="font-semibold">
                    Já cancelei a NFS-e e a fatura no SIIMP
                  </label>
                </div>
              </div>
            )}
            <label htmlFor="cancel-reason" className="sr-only">Motivo do cancelamento</label>
            <textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Motivo do cancelamento (obrigatório)"
              rows={3}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => { setCancelTarget(null); setCancelReason(""); setJaCanceladoNoSiimp(false); }}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
              >
                Voltar
              </button>
              <button
                disabled={!cancelReason.trim() || (cancelTarget.siimpEtapa > 0 && !jaCanceladoNoSiimp) || loadingId === cancelTarget.billingId}
                onClick={handleCancelConfirm}
                className="rounded-xl bg-red-500 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-red-600 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loadingId === cancelTarget.billingId ? "Cancelando…" : "Confirmar cancelamento"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação de EXCLUSÃO definitiva */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-dv-lg dark:bg-navy-800">
            <h3 className="mb-2 flex items-center gap-2 text-lg font-bold text-red-600 dark:text-red-400">
              <MdDeleteForever className="h-5 w-5" aria-hidden="true" /> Excluir lançamento
            </h3>
            <p className="mb-2 text-sm text-gray-600 dark:text-gray-300">
              Excluir <span className="font-semibold text-navy-700 dark:text-white">{fmt(deleteTarget.amount)}</span>{" "}
              de{" "}
              <span className="font-semibold text-navy-700 dark:text-white">
                {deleteTarget.projectName || deleteTarget.clientName}
              </span>
              {deleteTarget.projectName ? ` (${deleteTarget.clientName})` : " (nível cliente)"}?
            </p>
            <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
              Esta ação é <strong>permanente</strong> e remove o registro e seus vínculos de vez. Para apenas
              encerrar mantendo o histórico, use <strong>Cancelar</strong>.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
              >
                Voltar
              </button>
              <button
                disabled={loadingId === deleteTarget.billingId}
                onClick={handleDeleteConfirm}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-red-700 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loadingId === deleteTarget.billingId ? "Excluindo…" : "Excluir definitivamente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingTable;
